import { css } from 'lit';

export default css`
  :host {
    display: inline-block;
  }

  /* Two visual variants, selected by the data-variant attribute the parent
   * tab-group stamps on the host (shadow styles cannot see the group's own
   * attribute):
   *
   * classic (default) - filled folder tabs: solid primary fill, white text;
   * the active tab lifts to the surface colour and acts as its own state
   * indicator (the group hides its pill).
   *
   * chip - outlined chips: surface fill with a hairline inset ring; the
   * active chip goes transparent so the group's pill shows through. The
   * ring is an inset box-shadow, NOT a border: borders would change the
   * client sizes the group's indicator positioning measures.
   *
   * Base rules below are the classic look; the chip block overrides them.
   * --tab-border-radius is placement-aware, set by the parent tab-group.
   * The tab fills its host so vertical (start/end) strips stay uniform.
   *
   * The fills follow the host app's environment tint the same way the table2
   * header does: --overwrite-main / --overwrite-main-darker / --overwrite-light
   * are set on <body> by the dev/test environment stylesheets and inherit
   * through the shadow boundary, so the tabs match the table headers on every
   * environment. They fall back to the design-system primary for standalone
   * use. */
  .tab {
    display: flex;
    align-items: center;
    font-family: var(--webmodule-font-sans);
    font-size: var(--webmodule-font-size-small);
    font-weight: var(--webmodule-font-weight-semibold);
    border-radius: var(
      --tab-border-radius,
      var(--webmodule-border-radius-medium) var(--webmodule-border-radius-medium) 0 0
    );
    background-color: var(--overwrite-main, var(--webmodule-color-primary-600));
    color: var(--overwrite-light, var(--webmodule-color-neutral-0));
    padding: var(--webmodule-spacing-small) var(--webmodule-spacing-large);
    white-space: nowrap;
    user-select: none;
    -webkit-user-select: none;
    cursor: pointer;
    transition:
      var(--webmodule-animate-fast) color,
      var(--webmodule-animate-fast) background-color,
      var(--webmodule-animate-fast) box-shadow;
  }

  .tab:hover:not(.tab--disabled):not(.tab--active) {
    background-color: var(--overwrite-main-darker, var(--webmodule-color-primary-700));
  }

  .tab.tab--active:not(.tab--disabled) {
    background-color: var(--webmodule-color-neutral-0);
    color: var(--overwrite-main, var(--webmodule-color-primary-600));
    cursor: default;
  }

  /*
   * Chip variant
   */

  :host([data-variant='chip']) .tab {
    background-color: var(--webmodule-color-neutral-0);
    box-shadow: inset 0 0 0 1px var(--webmodule-color-neutral-200);
    color: var(--webmodule-color-neutral-700);
    padding: var(--webmodule-spacing-x-small) var(--webmodule-spacing-large);
  }

  :host([data-variant='chip']) .tab:hover:not(.tab--disabled):not(.tab--active) {
    background-color: var(--webmodule-color-neutral-0);
    color: var(--webmodule-color-neutral-900);
    box-shadow: inset 0 0 0 1px var(--webmodule-color-neutral-300);
  }

  :host([data-variant='chip']) .tab.tab--active:not(.tab--disabled) {
    background-color: transparent;
    box-shadow: none;
    /* sits on the group's pill, which follows the same environment tint */
    color: var(--overwrite-light, var(--webmodule-color-neutral-0));
  }

  /*
   * Shared
   */

  :host(:focus) {
    outline: transparent;
  }

  :host(:focus-visible) {
    outline: var(--webmodule-focus-border);
    outline-offset: var(--webmodule-focus-border-offset);
  }

  .tab.tab--closable {
    padding-inline-end: var(--webmodule-spacing-small);
  }

  .tab.tab--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .tab__close-button {
    font-size: var(--webmodule-font-size-small);
    margin-inline-start: var(--webmodule-spacing-x-small);
    /* webmodule-icon-button pins its own :host colour (neutral-600), which
     * blocks inheritance; follow the tab's text colour instead so the X
     * always matches the caption in both variants and states */
    color: inherit;
  }

  .tab__close-button::part(base) {
    color: currentColor;
    padding: var(--webmodule-spacing-3x-small);
  }

  .tab__close-button::part(base):hover,
  .tab__close-button::part(base):focus {
    color: currentColor;
    opacity: 0.75;
  }

  @media (forced-colors: active) {
    .tab.tab--active:not(.tab--disabled) {
      outline: solid 1px transparent;
      outline-offset: -3px;
    }
  }
`;
